import { Capacitor } from '@capacitor/core';
import BrotherPrint from '../plugins/BrotherPrintPlugin';

/**
 * Brother Bluetooth Label Printer Service
 * Wraps the native BrotherPrint Capacitor plugin for easy use from React components.
 * Falls back gracefully in the browser (non-native) environment.
 */

const STORAGE_KEY = 'brotherPrinterIdentifier';
const PRINTER_NAME_KEY = 'brotherPrinterName';

/**
 * Check if native Bluetooth printing is available (iOS and Android)
 */
export const isBrotherPrintAvailable = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Get saved printer identifier from localStorage
 */
export const getSavedPrinter = () => {
  const id = localStorage.getItem(STORAGE_KEY);
  const name = localStorage.getItem(PRINTER_NAME_KEY);
  return id ? { identifier: id, name: name || 'Ukjent' } : null;
};

/**
 * Save printer identifier to localStorage
 */
export const savePrinter = (identifier, name) => {
  localStorage.setItem(STORAGE_KEY, identifier);
  localStorage.setItem(PRINTER_NAME_KEY, name || 'Brother QL-1110NWBc');
};

/**
 * Clear saved printer
 */
export const clearSavedPrinter = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PRINTER_NAME_KEY);
};

/**
 * Search for Brother Bluetooth printers
 * @returns {Promise<Array>} List of found printers
 */
export const searchPrinters = async () => {
  if (!isBrotherPrintAvailable()) {
    return [];
  }
  try {
    const result = await BrotherPrint.searchPrinters();
    return result.printers || [];
  } catch (err) {
    console.error('[BrotherPrint] Search error:', err);
    return [];
  }
};

/**
 * Print a certificate label via Bluetooth
 * @param {Object} cert - Certificate data object
 * @param {string} printerIdentifier - Printer serial/MAC (or uses saved)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const printCertificateLabel = async (cert, printerIdentifier) => {
  if (!isBrotherPrintAvailable()) {
    return { success: false, message: 'Bluetooth-utskrift er kun tilgjengelig på iOS' };
  }

  const printerId = printerIdentifier || getSavedPrinter()?.identifier;
  if (!printerId) {
    return { success: false, message: 'Ingen skriver valgt. Søk og velg en skriver først.' };
  }

  try {
    const result = await BrotherPrint.printLabel({
      printerIdentifier: printerId,
      certificateNumber: cert.certificateNumber || '',
      steelGrade: cert.steelGrade || '',
      batch: cert.batch || '',
      charge: cert.charge || '',
      orderNumber: cert.orderNumber || '',
      issuedBy: cert.issuedBy || '',
      widthMm: cert.widthMm ? String(cert.widthMm) : '',
      lengthMm: cert.lengthMm ? String(cert.lengthMm) : '',
      thicknessMm: cert.thicknessMm ? String(cert.thicknessMm) : '',
      qrCodeBase64: cert.qrCode || '',
      copies: 1
    });
    return result;
  } catch (err) {
    console.error('[BrotherPrint] Print error:', err);
    return { success: false, message: 'Utskriftsfeil: ' + (err.message || err) };
  }
};

/**
 * Print an order card label via Bluetooth (same format as OrdersFlowWidget etikett)
 * @param {Object} orderData - Order data object
 * @param {string} printerIdentifier - Printer serial/MAC (or uses saved)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const printOrderLabel = async (orderData, printerIdentifier) => {
  if (!isBrotherPrintAvailable()) {
    return { success: false, message: 'Bluetooth-utskrift er kun tilgjengelig på native' };
  }

  const printerId = printerIdentifier || getSavedPrinter()?.identifier;
  if (!printerId) {
    return { success: false, message: 'Ingen skriver valgt. Søk og velg en skriver først.' };
  }

  try {
    const result = await BrotherPrint.printOrderLabel({
      printerIdentifier: printerId,
      orderId: String(orderData.orderId || ''),
      orderNumber: orderData.orderNumber || '',
      date: orderData.date || '',
      customer: orderData.customer || '',
      description: orderData.description || '',
      notes: orderData.notes || '',
      responsiblePerson: orderData.responsiblePerson || '',
      materials: orderData.materials || '',
      teams: orderData.teams || '',
      copies: 1
    });
    return result;
  } catch (err) {
    console.error('[BrotherPrint] Print order label error:', err);
    return { success: false, message: 'Utskriftsfeil: ' + (err.message || err) };
  }
};

/**
 * Print a raw base64 image via Bluetooth
 * @param {string} imageBase64 - Base64 encoded image
 * @param {string} printerIdentifier - Printer serial/MAC (or uses saved)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const printImage = async (imageBase64, printerIdentifier) => {
  if (!isBrotherPrintAvailable()) {
    return { success: false, message: 'Bluetooth-utskrift er kun tilgjengelig på iOS' };
  }

  const printerId = printerIdentifier || getSavedPrinter()?.identifier;
  if (!printerId) {
    return { success: false, message: 'Ingen skriver valgt.' };
  }

  try {
    const result = await BrotherPrint.printImage({
      imageBase64,
      printerIdentifier: printerId,
      copies: 1,
      autoCut: true
    });
    return result;
  } catch (err) {
    console.error('[BrotherPrint] Print image error:', err);
    return { success: false, message: 'Utskriftsfeil: ' + (err.message || err) };
  }
};

/**
 * Get printer connection status
 */
export const getPrinterStatus = async () => {
  if (!isBrotherPrintAvailable()) {
    return { connected: false, modelName: null };
  }
  try {
    return await BrotherPrint.getPrinterStatus();
  } catch (err) {
    return { connected: false, modelName: null };
  }
};
